import { useState, type FormEvent } from 'react';
import { getAuthor, postKnowledge, setAuthor } from '../lib/api';

// Inline correction: the moment someone sees a wrong or missing answer is the
// moment they can fix it, without leaving the chat or thinking about "the
// knowledge base" as a separate place to go.
//
// The question is carried over from the message that failed, so the entry is
// stored against the phrasing that actually came up rather than a tidied-up
// version of it.
interface Props {
  question: string;
  company: string;
  division: string;
  /** true when an entry was stored, false on cancel. */
  onDone: (saved: boolean) => void;
}

export function AddKnowledge({ question, company, division, onDone }: Props) {
  const [answer, setAnswer] = useState('');
  const [author, setAuthorInput] = useState(getAuthor() ?? '');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function submit(e: FormEvent) {
    e.preventDefault();
    if (!answer.trim() || !author.trim()) return;
    setSaving(true);
    setError(null);
    try {
      setAuthor(author.trim()); // remembered, so it is asked once not every time
      await postKnowledge({ question, answer: answer.trim(), author: author.trim(), company, division });
      onDone(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Opslaan mislukt.');
      setSaving(false);
    }
  }

  return (
    <form className="add-knowledge" onSubmit={submit}>
      <p className="ak-question">
        <span className="ak-label">Vraag</span> {question}
      </p>

      <textarea
        className="ak-answer"
        rows={3}
        autoFocus
        placeholder="Wat is het juiste antwoord?"
        value={answer}
        onChange={(e) => setAnswer(e.target.value)}
      />

      {/* Asked once and remembered. An entry nobody can trace back is the thing
          that makes curated knowledge impossible to review later. */}
      <input
        className="ak-author"
        placeholder="Jouw naam"
        value={author}
        onChange={(e) => setAuthorInput(e.target.value)}
      />

      {error && <p className="ak-error">{error}</p>}

      <div className="ak-actions">
        <button type="button" className="ak-cancel" onClick={() => onDone(false)}>Annuleren</button>
        <button type="submit" className="ak-save" disabled={saving || !answer.trim() || !author.trim()}>
          {saving ? 'Opslaan…' : 'Toevoegen aan kennisbank'}
        </button>
      </div>
      <p className="ak-note">
        Alleen zichtbaar voor collega's. Je naam en de datum worden bewaard zodat
        later te zien is wie dit heeft toegevoegd.
      </p>
    </form>
  );
}
